"use client";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";
import { useEffect, useMemo, useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

dayjs.updateLocale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s ago",
    s: "now",
    ss: "now",
    m: "now",
    mm: "%dm",
    h: "1h",
    hh: "%dh",
    d: "1d",
    dd: "%dd",
    M: "1mo",
    MM: "%dmo",
    y: "1y",
    yy: "%dy",
  },
});

interface Props {
  date: Date | string;
  format?: string;
  withTooltip?: boolean;
  className?: string;
  shorten?: boolean;
}

const getInterval = (date: Date | string): number | false => {
  const minute = 60 * 1000; // 1 minute in millisecond
  const now = dayjs();
  const dateObj = dayjs(date);
  const diffDays = now.diff(dateObj, "days");

  if (diffDays >= 1) {
    return false;
  }
  const diffHours = now.diff(dateObj, "hours");
  if (diffHours >= 1) {
    return 60 * minute;
  }
  const diffMinutes = now.diff(dateObj, "minutes");
  if (diffMinutes >= 1) {
    return minute;
  }

  return 1000;
};

export function DateFromNow({
  date,
  format,
  withTooltip = true,
  shorten = false,
  className,
}: Props) {
  const [fromNow, setFromNow] = useState(() => {
    const result = dayjs(date).fromNow(shorten);
    return result === "now ago" ? "now" : result;
  });

  const innerFormat = useMemo(() => "MMMM DD, YYYY [at] h:mm A", []);

  useEffect(() => {
    const updateDisplay = () => {
      const result = dayjs(date).fromNow(shorten);
      setFromNow(result === "now ago" ? "now" : result);
    };

    // Initial update
    updateDisplay();

    // For dates within the last day, use a 1-second interval for smooth updates
    // This is simpler than trying to dynamically change intervals
    const diffDays = dayjs().diff(dayjs(date), "days");
    if (diffDays >= 1) {
      return; // No updates needed for dates older than 1 day
    }

    const updater = setInterval(updateDisplay, 1000);

    return () => {
      clearInterval(updater);
    };
  }, [date, shorten]);

  const content = (
    <div className={className} suppressHydrationWarning>
      {fromNow}
    </div>
  );

  if (!withTooltip) {
    return content;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent>
        {dayjs(date).format(format || innerFormat)}
      </TooltipContent>
    </Tooltip>
  );
}
